import React, { type ReactNode, useCallback, useMemo, useState } from 'react';

import { loadEntryPoint } from 'react-relay';
import { mergeRefs } from 'use-callback-ref';

import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemProps,
	FlyoutMenuItemTrigger,
	FlyoutMenuItemTriggerProps,
} from '@atlaskit/navigation-system/side-nav-items/flyout-menu-item';
import Spinner from '@atlaskit/spinner';
import type {
	AnyEntryPoint,
	EntryPointRef,
	GetRuntimePropsFromEntryPoint,
	ParamsOfEntryPoint,
	RuntimePropsOfEntryPoint,
} from '@atlassian/entry-point-types';
import { usePressablePreloadRef } from '@atlassian/entry-point-use-trigger';
import {
	type ErrorCallback,
	type ErrorFallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { useClickToCloseRef } from './useClickToCloseRef';

const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;
const emptyEntryPointProps = {} as RuntimePropsOfEntryPoint<AnyEntryPoint>;

type UnionOmit<T, K extends string | number | symbol> = T extends unknown ? Omit<T, K> : never;

type CustomFlyoutProps = UnionOmit<FlyoutMenuItemProps, 'isOpen' | 'isDefaultOpen' | 'children'>;
type EntryPointPropsType<TEntryPoint> = GetRuntimePropsFromEntryPoint<TEntryPoint>;

export type Props<TEntryPoint> = {
	triggerProps: FlyoutMenuItemTriggerProps & { triggerRef?: React.Ref<HTMLButtonElement> };
	entryPoint: TEntryPoint;
	entryPointParams?: ParamsOfEntryPoint<TEntryPoint>;
	entryPointProps?: Omit<EntryPointPropsType<TEntryPoint>, 'onClose'> & {
		onClose?: () => void;
	};
	fallback?: ReactNode;
	errorFallback?: ErrorFallback;
	onError?: ErrorCallback;
} & CustomFlyoutProps;

export function FlyoutTrigger<TEntryPoint extends AnyEntryPoint>({
	triggerProps,
	entryPoint,
	entryPointParams,
	entryPointProps,
	fallback,
	errorFallback,
	onError,
	onOpenChange,
	...flyoutProps
}: Props<TEntryPoint>) {
	const environmentProvider = useRelayEnvironmentProvider();

	const id = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<any>).getModuleName() || 'unknown';
	}, [entryPoint]);

	const [isOpen, setIsOpen] = useState(false);
	const [content, setContent] = useState<ReactNode | null>(null);

	const handleOpen = useCallback(() => {
		setIsOpen(true);
	}, []);

	const handleClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	const onFlyoutOpenChange = useCallback(
		(open: boolean) => {
			setIsOpen(open);
			onOpenChange?.(open);
		},
		[onOpenChange],
	);

	const load = useCallback(
		() =>
			loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const onLoad = useCallback(
		({ reference: entryPointReference }: { reference: EntryPointRef<TEntryPoint> }) => {
			// avoid re-loading entry-point when closing flyout
			if (isOpen) {
				return;
			}

			handleOpen();

			setContent(
				<InternalEntryPointContainer
					id={id}
					entryPointReference={entryPointReference}
					fallback={fallback || <Spinner />}
					errorFallback={errorFallback}
					runtimeProps={
						{
							...(entryPointProps ?? emptyEntryPointProps),
							onClose: handleClose,
						} as unknown as EntryPointPropsType<TEntryPoint>
					}
					onError={onError}
				/>,
			);
		},
		[
			setContent,
			id,
			isOpen,
			entryPointProps,
			fallback,
			errorFallback,
			handleOpen,
			onError,
			handleClose,
		],
	);

	const triggerCloseRef = useClickToCloseRef({
		isOpen,
		handleClose,
	});

	const triggerRef = usePressablePreloadRef<EntryPointRef<TEntryPoint>>({
		load,
		onLoad,
	});

	const flyoutTriggerRef = useMemo(() => {
		const refs = [triggerRef, triggerCloseRef];
		if (triggerProps?.triggerRef) {
			refs.push(triggerProps.triggerRef as React.RefObject<HTMLButtonElement>);
		}
		return mergeRefs(refs) as React.Ref<HTMLButtonElement>;
	}, [triggerRef, triggerCloseRef, triggerProps.triggerRef]);

	const flyoutContent = useCallback(() => {
		return content;
	}, [content]);

	return (
		<FlyoutMenuItem {...flyoutProps} isOpen={isOpen} onOpenChange={onFlyoutOpenChange}>
			<FlyoutMenuItemTrigger {...triggerProps} ref={flyoutTriggerRef} />
			<FlyoutMenuItemContent onClose={handleClose}>{flyoutContent()}</FlyoutMenuItemContent>
		</FlyoutMenuItem>
	);
}
